import React, { useState, useMemo } from 'react';
import { FileText, BookOpen, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { journalPublications, conferencePublications, bookChapters, books } from '../data/cvData';

const Publications = () => {
  const [showAllJournals, setShowAllJournals] = useState(false);
  const [showAllConferences, setShowAllConferences] = useState(false);

  // Function to extract year from publication string
  const extractYear = (publication) => {
    const yearMatch = publication.match(/\b(19|20)\d{2}\b/g);
    if (yearMatch && yearMatch.length > 0) {
      return parseInt(yearMatch[yearMatch.length - 1]); // Get the last year mentioned
    }
    return 0;
  };

  // Sort publications by year (newest first)
  const sortedJournals = useMemo(() => {
    return [...journalPublications].sort((a, b) => extractYear(b) - extractYear(a));
  }, []);

  const sortedConferences = useMemo(() => {
    return [...conferencePublications].sort((a, b) => extractYear(b) - extractYear(a));
  }, []);

  const displayedJournals = showAllJournals ? sortedJournals : sortedJournals.slice(0, 10);
  const displayedConferences = showAllConferences ? sortedConferences : sortedConferences.slice(0, 5);

  return (
    <section id="publications" className="section-container bg-slate-50">
      <h2 className="section-title">Publications</h2>
      
      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="text-center border-2 border-slate-200">
            <CardContent className="p-6">
              <FileText size={40} className="text-[#1e3a8a] mx-auto mb-3" />
              <div className="text-4xl font-bold text-[#1e3a8a] mb-2">44</div>
              <div className="text-[#64748b] font-medium">Journal Publications</div>
            </CardContent>
          </Card>
          <Card className="text-center border-2 border-slate-200">
            <CardContent className="p-6">
              <Users size={40} className="text-[#1e3a8a] mx-auto mb-3" />
              <div className="text-4xl font-bold text-[#1e3a8a] mb-2">50+</div>
              <div className="text-[#64748b] font-medium">Conference/Conclave</div>
            </CardContent>
          </Card>
          <Card className="text-center border-2 border-slate-200">
            <CardContent className="p-6">
              <BookOpen size={40} className="text-[#1e3a8a] mx-auto mb-3" />
              <div className="text-4xl font-bold text-[#1e3a8a] mb-2">{books.length}</div>
              <div className="text-[#64748b] font-medium">Published Book</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="journals" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="journals">Journals</TabsTrigger>
            <TabsTrigger value="conferences">Conference/Conclave</TabsTrigger>
            <TabsTrigger value="books">Books</TabsTrigger>
            <TabsTrigger value="chapters">Book Chapters</TabsTrigger>
          </TabsList>

          <TabsContent value="journals">
            <div className="space-y-4">
              {displayedJournals.map((pub, index) => (
                <div key={index} className="publication-item p-5 bg-white rounded-lg border border-slate-200">
                  <p className="text-[#475569] leading-relaxed">
                    <span className="font-semibold text-[#1e3a8a] mr-2">[{index + 1}]</span>
                    {pub}
                  </p>
                </div>
              ))}
              {journalPublications.length > 10 && (
                <div className="text-center mt-6">
                  <Button
                    onClick={() => setShowAllJournals(!showAllJournals)}
                    variant="outline"
                    className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                  >
                    {showAllJournals ? 'Show Less' : `View All ${sortedJournals.length} Publications`}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="conferences">
            <div className="space-y-4">
              {displayedConferences.map((pub, index) => (
                <div key={index} className="publication-item p-5 bg-white rounded-lg border border-slate-200">
                  <p className="text-[#475569] leading-relaxed">
                    <span className="font-semibold text-[#1e3a8a] mr-2">[{index + 1}]</span>
                    {pub}
                  </p>
                </div>
              ))}
              {conferencePublications.length > 5 && (
                <div className="text-center mt-6">
                  <Button
                    onClick={() => setShowAllConferences(!showAllConferences)}
                    variant="outline"
                    className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                  >
                    {showAllConferences ? 'Show Less' : `View All ${sortedConferences.length} Papers`}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="books">
            <div className="space-y-4">
              {books.map((book, index) => (
                <div key={index} className="p-6 bg-white rounded-lg border-2 border-[#1e3a8a]">
                  <BookOpen size={24} className="text-[#1e3a8a] mb-3" />
                  <p className="text-[#475569] leading-relaxed text-lg">{book}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="chapters">
            <div className="space-y-6">
              {bookChapters.map((chapter, index) => (
                <div key={index} className="publication-item p-6 bg-white rounded-lg border-2 border-slate-200 hover:border-blue-300 transition-colors">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="font-bold text-[#1e3a8a] text-xl">[{index + 1}]</span>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-[#1e3a8a] mb-2">{chapter.title}</h3>
                      <p className="text-[#64748b] mb-2">
                        <span className="font-semibold">Authors:</span> {chapter.authors}
                      </p>
                      <p className="text-[#64748b] mb-1">
                        <span className="font-semibold">Book:</span> {chapter.book}
                      </p>
                      {chapter.series && (
                        <p className="text-[#64748b] mb-1">
                          <span className="font-semibold">Series:</span> {chapter.series}
                        </p>
                      )}
                      {chapter.pages && (
                        <p className="text-[#64748b] mb-1">
                          <span className="font-semibold">Pages:</span> {chapter.pages}
                        </p>
                      )}
                      <p className="text-[#64748b]">
                        <span className="font-semibold">Publisher:</span> {chapter.publisher}, {chapter.year}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Publications;